import { getUserCohortIdsForClient } from '../../utils/products';
import { getAmplitudeVariantPayload } from '../../utils/amplitudeCommon';
import { NO_TIER } from '../constants/centralLoyalty.constants';
import { getCentralLoyaltyConfig } from './featureFlagsSelector';

/**
 * Central loyalty tier, exactly as received.
 *
 * RelianceOne is the source of truth; AJIO never computes tier. It reaches the
 * UI as a cohort on the customerType API, which the server writes to the
 * `userCohortValues` cookie (see routes/middlewares/setUserCohort.js). The tier
 * arrives as one cohort among many (`cl_tier_gold`, `cl_tier_silver`, ...) on
 * the cart service (also present on plp/pdp). The configured
 * `centralLoyaltyConfig.tiers` values identify which cohort is the loyalty
 * one. The matched cohort is returned untouched — no mapping to GOLD / SILVER
 * / PLATINUM, no case changes. Every surface uses this value directly; only
 * the badge lookup turns it into an icon key.
 *
 * Returns '' when the user is on no tier — no loyalty cohort at all, or the
 * configured NONE cohort (`cl_tier_none`, not enrolled). A valid state: those
 * users fall back to BAU everywhere, which is what the `Boolean(tier)` gate on
 * each surface relies on.
 */
const getLoyaltyTier = (state: any, pageName = 'cart'): string => {
  const { metaData: { name } = {} } = state || {};
  const storeName = name && String(name).toLowerCase() !== 'ajio' ? name : 'rilfnl';
  const enableUniversalCohortFlow = getAmplitudeVariantPayload(
    'enableUniversalCohortFlow',
    state,
    false
  );

  const cohorts =
    getUserCohortIdsForClient(storeName, pageName, enableUniversalCohortFlow)?.split(/[|,]/) || [];

  const { tiers = {} } = getCentralLoyaltyConfig(state) || {};
  const tierCohorts = Object.keys(tiers).map((tierKey: string) => tiers[tierKey]);
  const noTierCohort = tiers[NO_TIER];

  const matchedCohort = cohorts
    .map((cohort: string) => String(cohort || '').trim())
    .find((cohort: string) => Boolean(cohort) && tierCohorts.includes(cohort));

  if (!matchedCohort || matchedCohort === noTierCohort) return '';

  return matchedCohort;
};

const getHasLoyaltyTier = (state: any): boolean => Boolean(getLoyaltyTier(state));

/**
 * Confirmed earn on a placed order, for TY and ODP.
 *
 * Reads the new contract fields (rOneEarnPoints / rOneEarnAmount) and falls back
 * to the legacy relianceOneEarnedAmount until backend confirms which is the
 * source of truth. Unlike the cart, this is order data — no tier lookup.
 */
const getOrderEarn = (
  order: any = {}
): { ronePoints: number; roneAmount: number; superCash: number } => {

  return {
    ronePoints: order?.relianceOneEarnPoints?.value || 0,
    roneAmount: order?.rOneEarnAmount?.value || order?.relianceOneEarnedAmount?.value || 0,
    superCash: order?.ajioCashEarnedAmt?.value || 0
  };
};

export { getLoyaltyTier, getHasLoyaltyTier, getOrderEarn };
